import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClientOpsFields } from "@/components/ClientOpsFields";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";
import { Plus, Prohibit, ArrowCounterClockwise } from "@phosphor-icons/react";

const empty = { name: "", email: "", password: "", role: "client", client_id: "" };

export default function UsersPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email and password required");
    if ((form.role === "client" || form.role === "client_ops") && !form.client_id) return toast.error("Select a client company");
    try {
      await api.post("/users", { ...form, client_id: ["client", "client_ops"].includes(form.role) ? form.client_id : null, permissions: form.role === "client_ops" ? (form.permissions || { view_photos: true, view_awb: true, download_docs: true, approve: true }) : null });
      if (form.role === "client_ops") toast.success("Client Operations User created successfully.");
      toast.success("User created");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const removeUser = async (id, email) => {
    await api.delete(`/users/${id}`);
    toast.success(`User ${email} deleted`);
    load();
  };

  const setActive = async (u, active) => {
    try {
      await api.patch(`/users/${u.user_id}`, { is_active: active });
      toast.success(active ? `${u.email} reactivated — they can log in again` : `${u.email} deactivated — login blocked, all orders & shipments kept`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update user");
    }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const isOps = user?.email === "ops@heliopolispharmaceuticals.com";

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success(`Role updated to ${role}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change role");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        subtitle="Access management"
        title="Users"
        actions={<Button className="rounded-sm" onClick={() => setOpen(true)} data-testid="add-user-button"><Plus size={16} className="mr-2" /> Add User</Button>}
      />
      <div className="border border-border rounded-md overflow-x-auto bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">Client</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u, i) => (
              <tr key={u.user_id} className={i % 2 ? "bg-muted/30" : ""} data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  {isOps && u.role !== "client" && u.user_id !== user?.user_id ? (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                      <SelectTrigger className="h-8 w-32 rounded-sm" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="team">Team</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="dispatch">Dispatcher</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-xs uppercase tracking-wide font-semibold">{u.role}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{u.role === "client" ? clientName(u.client_id) : "—"}</td>
                <td className="px-4 py-3">
                  {u.is_active ? (
                    <span className="inline-flex items-center rounded-sm border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-semibold uppercase text-green-700" data-testid={`status-${u.email}`}>Active</span>
                  ) : (
                    <span className="inline-flex items-center rounded-sm border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold uppercase text-red-700" title={u.deactivated_at ? `Deactivated ${new Date(u.deactivated_at).toLocaleDateString()}${u.deactivated_by ? ` by ${u.deactivated_by}` : ""}` : undefined} data-testid={`status-${u.email}`}>Deactivated</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {u.user_id !== user?.user_id && (
                    <div className="inline-flex items-center gap-1.5">
                      {u.is_active ? (
                        <Button variant="outline" size="sm" className="rounded-sm h-8 text-amber-700 border-amber-200 hover:bg-amber-50" title="Block login; keeps every order, shipment and record" onClick={() => setActive(u, false)} data-testid={`deactivate-user-${u.email}`}>
                          <Prohibit size={14} className="mr-1.5" /> Deactivate
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" className="rounded-sm h-8 text-green-700 border-green-200 hover:bg-green-50" onClick={() => setActive(u, true)} data-testid={`reactivate-user-${u.email}`}>
                          <ArrowCounterClockwise size={14} className="mr-1.5" /> Reactivate
                        </Button>
                      )}
                      <ConfirmDelete
                        testid={`delete-user-${u.email}`}
                        title={`Delete ${u.name}?`}
                        description={`This permanently removes the account ${u.email}. Prefer "Deactivate" for staff who left — it blocks login while keeping their history. This cannot be undone.`}
                        onConfirm={() => removeUser(u.user_id, u.email)}
                      />
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wide">Full name</Label><Input className="mt-1.5 rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Email</Label><Input type="email" className="mt-1.5 rounded-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Temporary password</Label><Input className="mt-1.5 rounded-sm" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="dispatch">Dispatcher</SelectItem>
                  <SelectItem value="client_ops">Client Operations User</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "client_ops" && (
              <ClientOpsFields form={form} setForm={setForm} />
            )}
            {(form.role === "client" || form.role === "client_ops") && (
              <div>
                <Label className="text-xs uppercase tracking-wide">Client company</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="user-client"><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {clients.length === 0 && <p className="text-xs text-muted-foreground mt-1">Add a client company first.</p>}
              </div>
            )}
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={save} data-testid="save-user">Create User</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
